import { MessageMedium } from 'constants/messageMediums';
import { selectLocaleImprovementsEnabled } from 'core/userContext';
import { useLocaleSettingsContext } from 'modules/campaigns/lib/contexts/LocaleSettingsContext';
import { Locale } from 'modules/domains/template';
import { DefaultLocaleSelectFormField } from 'modules/templateEditor/common/DefaultLocaleSelectFormField';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BUTTON_SIZES, PrimaryButton } from 'ui/button';
import { MODAL_SIZES, MODAL_VARIANTS, Modal, ModalBody, ModalFooter, ModalHeader } from 'ui/modal';

import { TemplateNameInput } from '../../components';
import { TEMPLATE_MENU_OPTIONS } from '../../constants';

import { createTemplate } from '../../ducks/actions';
import { selectIsTemplateAsyncProcessing } from '../../ducks/selectors';

type NewSimpleTemplateModalProps = {
  /* Function to Close Modal */
  closeModal: () => void;
  /* content strings to render */
  content: Record<string, unknown>;
  currentFolderId: number | null;
  /* Identifier of the template type selected */
  id: string;
  messageMedium: MessageMedium;
};

export const NewSimpleTemplateModal = ({
  closeModal = () => null,
  content,
  currentFolderId,
  id = '',
  messageMedium,
  ...props
}: NewSimpleTemplateModalProps) => {
  const { isTemplateDefaultLocaleModifiable } = useLocaleSettingsContext();
  const isLocaleImprovementsEnabled = useSelector(selectLocaleImprovementsEnabled);
  const [templateName, setTemplateName] = useState('');
  const [defaultLocale, setDefaultLocale] = useState('');
  const isTemplateAsyncProcessing = useSelector(selectIsTemplateAsyncProcessing);
  const dispatch = useDispatch();
  useEffect(() => document.getElementById(`${id}-template-name-input`)?.focus(), []);

  const {
    templateNameInput,
    createTemplateButtonText,
    // @ts-ignore
    modalHeader: { prefix, suffix },
  } = content;

  const handleDefaultLocale = (locale: Locale) => {
    setDefaultLocale(locale.tag);
  };

  // if option does not exist in TEMPLATE_MENU_OPTIONS render New template as title
  // @ts-ignore
  const modalHeader = TEMPLATE_MENU_OPTIONS[id]
    ? // @ts-ignore
      `${prefix} ${TEMPLATE_MENU_OPTIONS[id].modalLabel} ${suffix}`
    : `${prefix} ${suffix}`;

  return (
    <Modal
      withPortal
      id={`simple-${id}-template-modal`}
      isModalOpen={true}
      onClose={closeModal}
      size={MODAL_SIZES.MEDIUM}
      variant={MODAL_VARIANTS.DEFAULT}
      data-test={`simple-${id}-template-modal`}
      {...props}
    >
      <ModalHeader>{modalHeader}</ModalHeader>
      <ModalBody>
        <TemplateNameInput
          content={templateNameInput}
          templateName={templateName}
          setTemplateName={setTemplateName}
          id={`${id}-template-name-input`}
        />

        {isLocaleImprovementsEnabled && isTemplateDefaultLocaleModifiable && (
          <DefaultLocaleSelectFormField
            onSelect={handleDefaultLocale}
            isProjectLevelDefaultLocale
            messageMedium={messageMedium}
          />
        )}
      </ModalBody>
      <ModalFooter>
        <PrimaryButton
          isLoading={isTemplateAsyncProcessing}
          data-test="create-template-button"
          onClick={() =>
            dispatch(
              createTemplate({
                currentFolderId,
                name: templateName,
                defaultLocale,
                // @ts-ignore
                messageMedium: TEMPLATE_MENU_OPTIONS[id].slug,
              }),
            )
          }
          isDisabled={templateName.length === 0 || templateName.length > 255}
          size={BUTTON_SIZES.LARGE}
        >
          {/* @ts-ignore */}
          <span data-test="create-template-button-text">{createTemplateButtonText}</span>
        </PrimaryButton>
      </ModalFooter>
    </Modal>
  );
};
